import RightioLogo from "@assets/img/rightio-logo.svg?react";
import {
	AppBar,
	Box,
	Button,
	Container,
	ListItem,
	ListItemText,
	Toolbar,
} from "@mui/material";
import config from "@src/utilities/config";
import { useAppSelector } from "@store/hooks";
import { AuthContext } from "@utils/authProvider";
import { fullReset } from "@utils/general";
import React from "react";
import { Link, type NavigateFunction, useNavigate } from "react-router-dom";
import "../../../assets/css/NavMenu.css";
import useDebugInformation from "../../../hooks/useDebugInformation";
import useFeatureEnabled from "../../../hooks/useFeatureEnabled";
import { ui } from "../../../interfaces/interfaces";
import { useSidebarStore } from "../SideDrawer/useSideBarStore";
import Five9State from "./Five9State";
import Five9DevPlaceholder from "./Five9State/Five9DevPlaceholder";
import LoginMenu from "./LoginMenu";

const resetForm = async (navigate: NavigateFunction) => {
	await fullReset(navigate);
};

const AuthenticatedView: React.FC = React.memo(() => {
	const bookingFormLoaded = useAppSelector(
		({ state }) => state.BookingFormLoaded,
	);
	const formLocked = useAppSelector(({ state }) => state.FormLocked);
	const callStarted = useAppSelector(({ state }) => state.CallStarted);
	const callFrom = useAppSelector(({ ui }) => ui.CallFrom);
	const collapsed = useAppSelector(({ state }) => state.NavMenuCollapsed);
	const bookingId = useAppSelector(({ ui }) => ui.BookingId);
	const comboAgent = useAppSelector(({ ui }) => ui.ComboAgent);
	const navigate = useNavigate();
	const sideBarEnabled = useFeatureEnabled("AuthenticatedView", "SideBar");
	const { setShowSideBar } = useSidebarStore();
	const isDevEnvironment = config.debug;

	useDebugInformation("AuthenticatedView", {
		bookingFormLoaded,
		formLocked,
		callStarted,
		callFrom,
		collapsed,
	});

	return (
		<AppBar
			component="nav"
			position="static"
			color="transparent"
			sx={{ mb: 2 }}
			elevation={2}
		>
			<Container maxWidth="lg">
				<Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
					<Link
						to="/"
						style={{
							color: "inherit",
							textDecoration: "none",
							display: "flex",
							alignItems: "center",
						}}
					>
						<RightioLogo className="rightio-logo" />
					</Link>
					{isDevEnvironment ? <Five9DevPlaceholder /> : <Five9State />}

					<Box alignItems="center" gap={1} display="flex">
						{sideBarEnabled && (
							<ListItem disablePadding>
								<Button
									onClick={() => setShowSideBar(true)}
									color="primary"
									variant="contained"
									size="small"
									sx={{ textTransform: "uppercase" }}
								>
									<ListItemText primary="SideBar" />
								</Button>
							</ListItem>
						)}
						{isDevEnvironment && (
							<ListItem disablePadding>
								<Button
									component={Link}
									to="/"
									color="inherit"
									variant="contained"
									size="small"
									sx={{ textTransform: "uppercase" }}
								>
									<ListItemText primary="Home" />
								</Button>
							</ListItem>
						)}
						{isDevEnvironment && (
							<ListItem disablePadding>
								<Button
									onClick={() =>
										window
											?.open(`${config.api.wbs}/swagger/index.html`, "_blank")
											?.focus()
									}
									color="error"
									variant="contained"
									sx={{ textTransform: "uppercase" }}
									size="small"
								>
									<ListItemText primary="Swagger" />
								</Button>
							</ListItem>
						)}
						<LoginMenu />
						{bookingId > 0 && (
							<ListItem disablePadding>
								<Button
									onClick={() => resetForm(navigate)}
									color="error"
									variant="contained"
									size="small"
								>
									<ListItemText
										primary="Close Form"
										sx={{ textTransform: "uppercase", minWidth: 95 }}
									/>
								</Button>
							</ListItem>
						)}
						{/* {bookingFormLoaded &&
							!callStarted &&
							!formLocked &&
							callFrom &&
							callFrom.length > 0 && (
								<ListItem disablePadding>
									<Button
										onClick={() => makeCall({ phoneNumber: callFrom })}
										color="success"
										variant="contained"
										size="small"
										sx={{ textTransform: "uppercase" }}
									>
										<ListItemText primary="Call Back" />
									</Button>
								</ListItem>
							)} */}
						{isDevEnvironment && (
							<ListItem disablePadding>
								<Button
									onClick={() => {
										ui.AgentsModal = true;
									}}
									variant="contained"
									size="small"
									color="inherit"
									sx={{
										textTransform: "uppercase",
									}}
								>
									<ListItemText
										primary={
											comboAgent?.full_name ? comboAgent.full_name : "Agents"
										}
									/>
								</Button>
							</ListItem>
						)}
					</Box>
				</Toolbar>
			</Container>
		</AppBar>
	);
});

const AnonymousView: React.FC = React.memo(() => {
	const collapsed = useAppSelector(({ state }) => state.NavMenuCollapsed);
	useDebugInformation("AnonymousView", { collapsed });

	return (
		<AppBar
			component="nav"
			position="static"
			color="transparent"
			sx={{ mb: 2 }}
			elevation={2}
		>
			<Container maxWidth="lg">
				<Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
					<Link
						to="/"
						style={{
							color: "inherit",
							textDecoration: "none",
							display: "flex",
							alignItems: "center",
						}}
					>
						<RightioLogo className="rightio-logo" />
					</Link>
					<Box
						alignItems="center"
						gap={1}
						justifyContent="flex-end"
						display="flex"
					>
						<ListItem disablePadding>
							<Button
								component={Link}
								to="/"
								color="inherit"
								sx={{ textTransform: "uppercase" }}
							>
								<ListItemText primary="Home" />
							</Button>
						</ListItem>
						<LoginMenu />
					</Box>
				</Toolbar>
			</Container>
		</AppBar>
	);
});

type Props = {
	window?: () => Window;
};

const NavMenu: React.FC<Props> = (_props: Props) => {
	const { isAuthenticated } = React.useContext(AuthContext);

	return (
		<div>{isAuthenticated ? <AuthenticatedView /> : <AnonymousView />}</div>
	);
};

export default React.memo(NavMenu);
